import { WithPassiveTooltip } from '@/tooltip'
import { TreeAddRemoveAction, TreeHistoryStep } from '@/types/embed'
import classNames from 'classnames'
import React, { useContext } from 'react'
import { FocusAction, TreeDataContext } from './common'

function HistoryRespecStep({
  step,
  active,
  setFocus,
  ...props
}: {
  step: TreeAddRemoveAction
  active?: boolean
  setFocus: (action: FocusAction | undefined) => void
} & React.HTMLAttributes<HTMLDivElement>) {
  const onMouseEnter = React.useCallback(
    () =>
      setFocus({
        nodes: [...(step.add ?? []), ...(step.remove ?? [])]
      }),
    [step, setFocus]
  )
  const onClick = React.useCallback(
    () =>
      setFocus({
        nodes: [...(step.add ?? []), ...(step.remove ?? [])],
        center: true
      }),
    [step, setFocus]
  )
  const onMouseLeave = React.useCallback(() => setFocus(undefined), [setFocus])
  if (!props.onClick) {
    props.onClick = onClick
  }
  return (
    <div
      className={classNames('poe-step poe-respec', { 'poe-active': active })}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...props}
    />
  )
}

export function HistoryStep({
  id,
  active,
  setFocus,
  ...props
}: {
  id: TreeHistoryStep
  active?: boolean
  setFocus: (action: FocusAction | undefined) => void
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'id'>) {
  const data = useContext(TreeDataContext)
  const node = typeof id === 'object' ? undefined : data.node(id)

  const onMouseEnter = React.useCallback(
    () =>
      setFocus({
        nodes: typeof id === 'number' ? [id] : []
      }),
    [id, setFocus]
  )
  const onMouseLeave = React.useCallback(() => setFocus(undefined), [setFocus])
  const onClick = React.useCallback(
    () =>
      setFocus({
        nodes: typeof id === 'number' ? [id] : [],
        center: true
      }),
    [id, setFocus]
  )
  if (typeof id === 'object') {
    return (
      <HistoryRespecStep
        step={id}
        active={active}
        setFocus={setFocus}
        {...props}
      />
    )
  }
  if (!node) return null
  const className = ['poe-step']
  if (active) className.push('poe-active')
  if (node.skill.is_jewel_socket) {
    className.push('poe-notable')
  } else if (node.skill.is_ascendancy_root) {
    return null
  } else if (node.skill.starting_node) {
    return null
  } else if (node.skill.is_notable) {
    className.push('poe-notable')
  } else if (node.skill.is_keystone) {
    className.push('poe-keystone')
  } else if (node.skill.mastery) {
    className.push('poe-notable')
  } else if (node.skill.is_just_icon) {
    return null
  }
  if (!props.onClick) {
    props.onClick = onClick
  }
  return (
    <WithPassiveTooltip
      version={data.state.version}
      id={id}
      masteryIndex={node.masteryIndex}
      atlas={data.atlas}>
      <div
        className={className.join(' ')}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        {...props}
      />
    </WithPassiveTooltip>
  )
}
